import React, { useEffect, useRef, useState } from "react";
import PurchaseOrderForm from "./PurchaseOrderForm.main";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";

import axios from "axios";
import Swal from "sweetalert2";

const ViewPurchaseOrder = () => {
  let location = useLocation();
  let navigate = useNavigate();
  const serverURL = process.env.REACT_APP_URL;

  const token = localStorage.getItem("token");
  const [showActionBar, setShowActionBar] = useState(false);
  const scrollRef = useRef(null);
  const termsRef = useRef(null);
  const footerRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const isPOData = location.state?.poData;
  const poStatus = location?.state?.poStatus;
  const poLink = location?.state?.pdfLink;
  const poId = location?.state?.poId;
  const vendorId = location?.state?.vendorId;
  const pdfRef = useRef();
  useEffect(() => {
    const element = scrollRef.current;

    const handleScroll = () => {
      setShowActionBar(element.scrollTop > 0);
    };

    element?.addEventListener("scroll", handleScroll);

    return () => {
      element?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 5;

      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;

      const footerGap = 3;

      const drawPageBorder = () => {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);

        pdf.rect(margin, margin, contentWidth, contentHeight);
      };

      const mainCanvas = await html2canvas(pdfRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const mainImg = mainCanvas.toDataURL("image/jpeg", 0.8);

      const mainImgWidth = contentWidth;

      const mainImgHeight =
        (mainCanvas.height * mainImgWidth) / mainCanvas.width;

      let mainHeightLeft = mainImgHeight;
      let mainSourceY = 0;

      pdf.addImage(
        mainImg,
        "JPEG",
        margin,
        margin - mainSourceY,
        mainImgWidth,
        mainImgHeight,
      );

      drawPageBorder();

      mainHeightLeft -= contentHeight;
      mainSourceY += contentHeight;

      // Remaining pages
      while (mainHeightLeft > 0) {
        pdf.addPage();

        pdf.addImage(
          mainImg,
          "JPEG",
          margin,
          margin - mainSourceY,
          mainImgWidth,
          mainImgHeight,
        );

        drawPageBorder();

        mainHeightLeft -= contentHeight;
        mainSourceY += contentHeight;
      }

      // ==========================================
      // 2. FOOTER
      // ==========================================

      const footerCanvas = await html2canvas(footerRef.current, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const footerImg = footerCanvas.toDataURL("image/jpeg", 0.8);

      const footerWidth = contentWidth;

      const footerHeight =
        (footerCanvas.height * footerWidth) / footerCanvas.width;

      // ==========================================
      // CALCULATE LAST PAGE SPACE
      // ==========================================

      let usedHeightOnLastPage;

      if (mainImgHeight <= contentHeight) {
        usedHeightOnLastPage = mainImgHeight;
      } else {
        usedHeightOnLastPage = mainImgHeight % contentHeight;

        if (usedHeightOnLastPage < 0.1) {
          usedHeightOnLastPage = contentHeight;
        }
      }

      const availableFooterSpace =
        contentHeight - usedHeightOnLastPage - footerGap;

      // ==========================================
      // FOOTER
      // ==========================================

      if (footerHeight <= availableFooterSpace) {
        const footerY = margin + usedHeightOnLastPage + footerGap;

        pdf.addImage(
          footerImg,
          "JPEG",
          margin,
          footerY,
          footerWidth,
          footerHeight,
        );

        drawPageBorder();
      } else {
        pdf.addPage();

        pdf.addImage(
          footerImg,
          "JPEG",
          margin,
          margin,
          footerWidth,
          footerHeight,
        );

        drawPageBorder();
      }

      const termsCanvas = await html2canvas(termsRef.current, {
        scale: 1,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const termsWidth = contentWidth;

      const pixelsPerPage = (contentHeight / termsWidth) * termsCanvas.width;

      let termsSourceY = 0;

      while (termsSourceY < termsCanvas.height) {
        // Always start terms on a new page
        pdf.addPage();

        const remainingHeight = termsCanvas.height - termsSourceY;

        const sliceHeight = Math.floor(
          Math.min(pixelsPerPage, remainingHeight),
        );

        const pageCanvas = document.createElement("canvas");

        pageCanvas.width = termsCanvas.width;
        pageCanvas.height = sliceHeight;

        const ctx = pageCanvas.getContext("2d");

        ctx.fillStyle = "#ffffff";

        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

        ctx.drawImage(
          termsCanvas,
          0,
          Math.floor(termsSourceY),
          termsCanvas.width,
          sliceHeight,
          0,
          0,
          termsCanvas.width,
          sliceHeight,
        );

        const pageImg = pageCanvas.toDataURL("image/jpeg", 0.65);

        const pageImgHeight = (sliceHeight * termsWidth) / termsCanvas.width;

        pdf.addImage(
          pageImg,
          "JPEG",
          margin,
          margin,
          termsWidth,
          pageImgHeight,
        );

        drawPageBorder();

        termsSourceY += sliceHeight;
      }

      // ==========================================
      // RETURN PDF BLOB
      // ==========================================

      const pdfBlob = pdf.output("blob");

      return pdfBlob;
    } catch (error) {
      console.error("PDF generation error:", error);

      throw error;
    }
  };
  const downloadPDF = async () => {
    try {
      // 1. Generate PDF
      const pdfBlob = await generatePDF();

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const pdfLink = document.createElement("a");
      pdfLink.href = pdfUrl;
      pdfLink.download = `PO-${isPOData.po_number}.pdf`;

      document.body.appendChild(pdfLink);
      pdfLink.click();
      document.body.removeChild(pdfLink);

      URL.revokeObjectURL(pdfUrl);

      // 2. Call API to generate Excel
      const response = await axios.get(
        `${serverURL}/purchaseOrder/downloadAssignedStoresExcel/${poId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      // Create downloadable file
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `PO_Report_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download PDF/Excel error:", error);
    }
  };

  const handleStoresAssignModal = async () => {
    try {
      setPdfLoading(true);
      if (!poLink) {
        const pdfBlob = await generatePDF();

        const fileName = `PO-${isPOData.po_number}.pdf`;

        const pdfFile = new File([pdfBlob], fileName, {
          type: "application/pdf",
        });

        const maxSize = 2 * 1024 * 1024;

        if (pdfFile.size > maxSize) {
          Swal.fire({
            icon: "error",
            title: "PDF Too Large",
            text: "Generated PDF cannot be larger than 2 MB.",
          });

          return;
        }

        const formData = new FormData();

        formData.append("files", pdfFile);

        const token = localStorage.getItem("token");

        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_URL}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!uploadResponse?.data?.success) {
          throw new Error(uploadResponse?.data?.message || "PDF upload failed");
        }

        const uploadedFile = uploadResponse?.data?.data?.[0];

        await axios.put(
          `${serverURL}/purchaseOrder/editPO/${isPOData.id}`,
          {
            pdfLink: uploadedFile,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }
      setPdfLoading(false);

      navigate("/adminportal/assignStores", {
        state: {
          poData: location.state?.poData,
          poId: location?.state.poId,
          vendorId: location?.state.vendorId,
          campaignId: location?.state.campaignId,

          //   poDocument: uploadedFile,
        },
      });
    } catch (error) {
      console.error("Error generating/uploading PO PDF:", error);
      setPdfLoading(false);

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to generate or upload PO PDF.",
      });
    }
  };

  const handleViewLinkedStores = async () => {
    navigate(`/adminportal/linkedStores/${poId}`, {
      poData: isPOData,
      poId: location?.state.poId,
    });
  };
  return (
    <div
      ref={scrollRef}
      className="h-[89vh] overflow-y-scroll bg-gray-100 p-4 md:p-8"
    >
      {" "}
      {
        <div
          className={`sticky -top-8 z-50 flex w-full items-center justify-between gap-3
      border-b bg-white px-4 py-3 shadow-md
      transition-all duration-300
      ${
        showActionBar
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0"
      }`}
        >
          <div onClick={handleViewLinkedStores}>
            <button className=" text-[14px] text-blue-400 shadow-md p-2 border border-blue-400 rounded-md">
              View Stores
              <ArrowOutwardIcon
                sx={{ height: 18, width: 18, marginBottom: 0.5 }}
              />
            </button>
          </div>
          <div className="flex justify-center gap-2 items-center">
            <button
              onClick={downloadPDF}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 mr-2"
            >
              ⬇ Download PDF
            </button>
            {poStatus == "Sent" &&
              (pdfLoading ? (
                <>
                  <p className="border border-1 rounded-md p-2 text-blue-500 border-blue-500">
                    Loading...{" "}
                  </p>
                </>
              ) : (
                <button
                  onClick={handleStoresAssignModal}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Assign Stores
                </button>
              ))}
          </div>
        </div>
      }{" "}
      <PurchaseOrderForm
        pdfRef={pdfRef}
        isPOData={isPOData}
        isViewMode={true}
        termsRef={termsRef}
        footerRef={footerRef}
      />
    </div>
  );
};

export default ViewPurchaseOrder;
